import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Wordmark } from "./Logo";
import { ArrowUpRight } from "lucide-react";
import { waLink } from "../data/site";
import { useOpenStatus } from "../hooks/useOpenStatus";

const LINES = ["Impressão que", "valoriza a", "sua marca."];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, i, italic }) => (
  <span className={`block overflow-hidden pt-[0.18em] pb-[0.08em] px-[0.1em] -mx-[0.1em] ${i ? "-mt-[0.18em]" : ""}`}>
    <motion.span
      className={`block ${italic ? "italic text-alga" : ""}`}
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.12, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div className="[perspective:1100px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        data-testid="hero-tilt-card"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 40, rotateZ: -4 }}
        animate={{ opacity: 1, y: 0, rotateZ: -3 }}
        transition={{ duration: 1.2, delay: 0.7, ease }}
        className="relative aspect-[4/5] w-full rounded-[2px] bg-paper-2 shadow-[0_40px_80px_-30px_rgba(17,22,20,0.45)]"
      >
        <img src="/img/p-cartoes.jpg" alt="Cartões de visita impressos" className="absolute inset-0 h-full w-full object-cover rounded-[2px]" />
        <div className="absolute inset-0 grain opacity-40 mix-blend-multiply" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute -bottom-6 -left-5 bg-paper px-4 py-3 border border-ink/10">
          <p className="font-display uppercase text-sm sm:text-base leading-tight whitespace-nowrap">Arte <span className="text-alga">+</span> Impressão</p>
          <p className="font-display uppercase text-sm sm:text-base leading-tight whitespace-nowrap"><span className="text-alga">+</span> Acabamento</p>
        </div>
        <div style={{ transform: "translateZ(40px)" }} className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-alga text-paper grid place-items-center font-mono text-[11px] tracking-widest">
          CMYK
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const open = useOpenStatus();
  return (
    <section ref={ref} data-testid="hero-section" data-hide-wa className="relative px-5 sm:px-10 pt-6 pb-24 lg:pb-28">
      <header className="flex items-center justify-between">
        <Wordmark />
        <span data-testid="status-badge" data-open={open} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft">
          <span className="relative flex h-2 w-2">{open && <span className="absolute inset-0 rounded-full bg-alga animate-ping opacity-60" />}<span className={`relative h-2 w-2 rounded-full ${open ? "bg-alga" : "bg-ink-soft"}`} /></span>
          <span className="hidden sm:inline">{open ? "Aceitando pedidos" : "Fechado agora"}</span><span className="sm:hidden">{open ? "Aberto" : "Fechado"}</span>
        </span>
      </header>
      <div className="mt-14 lg:mt-20 grid lg:grid-cols-12 gap-12 lg:gap-8 items-end">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-ink-soft mb-6">
            Design Estratégico e Impressão Digital
          </motion.p>
          <h1 data-testid="hero-title" className="font-display uppercase leading-[0.95] text-[9vw] sm:text-[7.5vw] lg:text-[5vw] pr-3 sm:pr-0">
            {LINES.map((l, i) => <MaskLine key={l} i={i} italic={i === 2}>{l}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-8 max-w-md text-base sm:text-lg text-ink-soft leading-relaxed">
            Design estratégico, impressão digital profissional e comunicação visual para empresas que buscam qualidade, agilidade e uma apresentação profissional.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={waLink("Olá, Alfa Gráfica Digital! Gostaria de solicitar um orçamento.")} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-orcamento" className="group inline-flex items-center justify-between gap-4 rounded-full bg-ink text-paper pl-6 pr-2 py-2 text-sm font-bold uppercase tracking-wide transition-[background-color,transform] duration-300 hover:bg-alga active:scale-95">
              Solicitar orçamento
              <span className="grid place-items-center h-9 w-9 rounded-full bg-alga text-paper transition-[background-color,transform] duration-500 group-hover:bg-ink group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </a>
            <a href="#produtos" data-testid="hero-cta-produtos" className="inline-flex items-center justify-center rounded-full border-2 border-ink px-6 py-3 text-sm font-bold uppercase tracking-wide transition-[background-color,color,transform] duration-300 hover:bg-ink hover:text-paper active:scale-95">
              Conheça nossos produtos
            </a>
          </motion.div>
        </div>
        <motion.div style={{ y }} className="lg:col-span-5 lg:col-start-8 max-w-[270px] sm:max-w-sm w-full mx-auto lg:mx-0 px-4">
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};
